import React, { useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as ProductCreators } from 'store/products/products-duck.js';
import ProductPreview from 'components/product-preview';
import PropTypes from 'prop-types';
import QueryString from 'qs';
import ProductSidebarMenu from './filters/categoryFilter.jsx';

const ProductsPage = ({
  storeProducts,
  storeFilters,
  productsFetchStoreAsync,
  productsSetStoreFilters,
  storeTotalCount,
}) => {
  const location = useLocation();
  const { categoryId, subCategoryId, searchTerm } = QueryString.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const fetch = useCallback(() => {
    const filters = {
      categoryId,
      subCategoryId,
      page: storeFilters.page || 1,
      searchTerm: searchTerm || '',
      orderBy: storeFilters.orderBy !== 'standard' ? storeFilters.orderBy : '',
    };

    productsSetStoreFilters(filters);
    productsFetchStoreAsync(filters);
  }, [
    categoryId,
    subCategoryId,
    storeFilters.page,
    searchTerm,
    storeFilters.orderBy,
    productsFetchStoreAsync,
    productsSetStoreFilters,
  ]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handlePageChange = (_, value) => {
    const newFilters = { ...storeFilters, page: value };
    productsSetStoreFilters(newFilters);
    productsFetchStoreAsync(newFilters);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 4,
        p: 2,
        bgcolor: 'black',
        color: 'white',
      }}
    >
      <ProductSidebarMenu />
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Produtos
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl variant="standard" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: 'white' }}>Ordenar por</InputLabel>
              <Select
                value={storeFilters.orderBy || 'standard'}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const newFilters = {
                    ...storeFilters,
                    orderBy: newValue === 'standard' ? '' : newValue,
                    page: 1,
                  };
                  productsSetStoreFilters(newFilters);
                }}
                sx={{ color: 'white', borderBottom: '1px solid white' }}
              >
                <MenuItem value="standard">Padrão</MenuItem>
                <MenuItem value="price_asc">Menor preço</MenuItem>
                <MenuItem value="price_desc">Maior preço</MenuItem>
                <MenuItem value="name_asc">Nome A-Z</MenuItem>
                <MenuItem value="name_desc">Nome Z-A</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Grid container mb={2} justifyContent={'space-around'}>
          {storeProducts?.map((product) => (
            <Grid item xs={12} sm={6} md={3} key={product.id}>
              <ProductPreview product={product} />
            </Grid>
          ))}
        </Grid>

        <Box display="flex" justifyContent="center">
          <Pagination
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            sx={{ button: { color: '#92b21f' } }}
            count={Math.ceil(storeTotalCount / 10)}
            page={storeFilters.page || 1}
          />
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  storeProducts: state.Product.storeProducts,
  storeFilters: state.Product.storeFilters,
  storeTotalCount: state.Product.storeTotalCount,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(ProductCreators, dispatch);

ProductsPage.propTypes = {
  storeTotalCount: PropTypes.number.isRequired,
  storeProducts: PropTypes.array.isRequired,
  storeFilters: PropTypes.object.isRequired,
  productsFetchStoreAsync: PropTypes.func.isRequired,
  productsSetStoreFilters: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductsPage);
